import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CropFieldsResponse, Employee } from 'src/app/shared/models';
import { UrlHelpers } from 'src/app/shared/services/urlHelpers';
import { Environment } from 'src/environment';

import {
  CreateOrderRequest,
  CreateOrderResponse,
  CropAidResponse,
  FertiliserOrderFormParameters,
  CustomerFertilizerStock,
  FertilizerStockRow,
  GetArticlesByCropAidsResponse,
  GetCropAidsResponse,
  GetFertiliserOrderplanResponse,
  OrderLine,
  UpsertCustomerFertilizerStockRequest,
  UpsertOrderLineRequest,
  OptimizeOrderLineResponse,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class FertilisersApiService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected baseUrl = this.env.API_BASE_URL + '/service/orderplanning/secure';
  protected arableServiceUrl = this.env.APIM_BASE_URL + '/arable-service';
  protected fieldAdviceUrl = this.env.FIELD_ADVICE_REBUILD_DONE ? 
  this.env.APIM_BASE_URL + '/arable-service/advice/' :
  this.env.API_BASE_URL + '/service/field-advice/secure/';

  getCropAids(): Observable<CropAidResponse[]> {
    return this.http.get<GetCropAidsResponse>(`${this.arableServiceUrl}/crop-aid?productType=1`).pipe(map((cropAidResponse) => cropAidResponse.cropAids));
  }

  getProprietaryCropAids(cropYear: number): Observable<CropAidResponse[]> {
    return this.http
      .get<GetCropAidsResponse>(`${this.arableServiceUrl}/crop-aid/proprietary/${cropYear}`)
      .pipe(map((cropAidResponse) => cropAidResponse.cropAids));
  }

  getCropFields(cropYear: number) {
    return this.http.get<CropFieldsResponse>(`${this.baseUrl}/cropfield/${cropYear}`);
  }

  getArticlesByCropAids(cropAidIds: number[]): Observable<GetArticlesByCropAidsResponse> {
    return this.http.get<GetArticlesByCropAidsResponse>(`${this.baseUrl}/cropaid?${UrlHelpers.getParamString('cropAidIds', cropAidIds)}`);
  }

  saveLocationCropFields(cropYear: number, cropFieldIds: number[]) {
    return this.http.post(`${this.baseUrl}/cropfield/shipto`, { cropYear, cropFieldIds });
  }

  getLoggedInEmployee() {
    return this.http.get<Employee>(`${this.fieldAdviceUrl}loggedinemployee`);
  }

  getFertiliserOrderplan(cropYear: number): Observable<GetFertiliserOrderplanResponse> {
    return this.http.get<GetFertiliserOrderplanResponse>(`${this.baseUrl}/orderplan/fertiliser/${cropYear}`);
  }

  createOrder(year: number, fertiliserOrderFormParameters: FertiliserOrderFormParameters) {
    if (this.env.ENABLE_SEND_ORDER) {
      const request: CreateOrderRequest = { year, preferredOrderDate: fertiliserOrderFormParameters.preferredOrderDate };
      return this.http.post<CreateOrderResponse>(`${this.baseUrl}/orderplan/fertiliser/create`, request);
    }

    return of({ id: -1 } as CreateOrderResponse);
  }

  getCustomerFertilizerStock(cropYear: number) {
    return this.http.get<CustomerFertilizerStock>(`${this.baseUrl}/customerfertilizerstock/cropYear/${cropYear}`).pipe(
      map((d) => d),
      catchError(() => of({ fertilizerStock: [] } as CustomerFertilizerStock)),
    );
  }

  upsertCustomerFertilizerStock(cropYear: number, customerFertilizerStock: FertilizerStockRow[]) {
    const request: UpsertCustomerFertilizerStockRequest = {
      cropYear,
      fertilizerStock: customerFertilizerStock,
    };

    return this.http.post(`${this.baseUrl}/customerfertilizerstock`, request);
  }

  deleteOrderLine(id: number) {
    return this.http.delete(`${this.baseUrl}/orderline/${id}`);
  }

  deleteOrderLines(ids: number[], year: number) {
    return this.http.post(`${this.baseUrl}/orderline/delete-orderlines`, { ids, year });
  }

  upsertOrderLine(year: number, orderLine: OrderLine, cropAidId: number) {
    const request: UpsertOrderLineRequest = {
      ...orderLine,
      year,
      cropAidId,
    };

    return this.http.post(`${this.baseUrl}/orderline`, request);
  }

  optimizeOrderLine(year: number, orderLineId: number): Observable<OptimizeOrderLineResponse> {
    return this.http.post<OptimizeOrderLineResponse>(`${this.baseUrl}/orderplan/fertiliser/optimize/${year}/${orderLineId}`, {});
  }
}
